import { useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import './App.css';
import { usePortalContext }      from './hooks/usePortalContext';
import { useFreeStatus }         from './hooks/useFreeStatus';
import { postFree, postVoucher } from './lib/api';
import PortalCard  from './components/PortalCard';
import SuccessCard from './components/SuccessCard';
import TermsPage   from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';

type Phase = 'idle' | 'busy-free' | 'busy-voucher' | 'ok' | 'err-free' | 'err-voucher';
type View  = 'portal' | 'terms' | 'privacy';

const App = () => {
  const ctx             = usePortalContext();
  const [free, setFree] = useFreeStatus(ctx.clientMac);
  const [phase,   setPhase]   = useState<Phase>('idle');
  const [errMsg,  setErrMsg]  = useState('');
  const [voucher, setVoucher] = useState('');
  const [view,    setView]    = useState<View>('portal');
  const [codeFromQR, setCodeFromQR] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      setVoucher(code.toUpperCase());
      setCodeFromQR(true);
    }
  }, []);

  if (view === 'terms')   return <TermsPage   onBack={() => setView('portal')} />;
  if (view === 'privacy') return <PrivacyPage onBack={() => setView('portal')} />;
  if (phase === 'ok')     return <SuccessCard />;

  const connectFree = async () => {
    if (phase.startsWith('busy') || free.status === 'cooldown' || free.status === 'checking') return;
    setPhase('busy-free');
    try {
      const { ok, status, json } = await postFree(ctx);
      if (!ok) {
        if (status === 429) { setFree({ status: 'cooldown', remaining: json.remaining as number }); setPhase('idle'); return; }
        throw new Error((json.error as string | undefined) ?? 'FAILED');
      }
      setPhase('ok');
      setTimeout(() => { window.location.href = ctx.redir; }, 2500);
    } catch {
      setPhase('err-free');
      setErrMsg('Verbindungsfehler – bitte nochmal versuchen.');
    }
  };

  const connectVoucher = async (e: FormEvent) => {
    e.preventDefault();
    if (!voucher || phase.startsWith('busy')) return;
    setPhase('busy-voucher');
    try {
      const { ok, json } = await postVoucher(ctx, voucher);
      if (!ok) throw new Error((json.error as string | undefined) ?? 'FAILED');
      setPhase('ok');
      setTimeout(() => { window.location.href = ctx.redir; }, 2500);
    } catch (err) {
      setPhase('err-voucher');
      const msg = err instanceof Error ? err.message : '';
      setErrMsg(
        msg === 'Omada API error'   ? 'Verbindungsfehler – bitte nochmal versuchen.' :
        msg === 'Code abgelaufen'   ? 'Code abgelaufen – bitte einen neuen bestellen.' :
                                      'Ungültiger Code – bitte frag das Personal.'
      );
    }
  };

  return (
    <div className="portal">
      <PortalCard
        phase={phase}
        free={free}
        errMsg={errMsg}
        voucher={voucher}
        codeFromQR={codeFromQR}
        onVoucherChange={v => { setVoucher(v); if (phase.startsWith('err')) setPhase('idle'); }}
        onConnect={connectFree}
        onSubmit={connectVoucher}
        onShowTerms={() => setView('terms')}
        onShowPrivacy={() => setView('privacy')}
      />
    </div>
  );
};

export default App;
